import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Body1,
  Body1Strong,
  Button,
  Card,
  Caption1,
  Dropdown,
  Input,
  makeStyles,
  Option,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabList,
  createTableColumn,
  tokens,
  useTableFeatures,
  mergeClasses,
} from '@fluentui/react-components';
import { AddFilled, OpenRegular, SearchRegular } from '@fluentui/react-icons';

import { FoldersNavDrawer, TFolder, TPath, useItemTableStyles } from '@modules/items';
import { Path, SmartTableCell } from '@shared/components';
import { useFolderPathAsBreadcrumbs, usePersistedColumnSizing } from '@shared/hooks';

import {
  CHANGE_DASHBOARD_TABS,
  CHANGE_OWNER_OPTIONS,
  CHANGE_STATUS_OPTIONS,
  CHANGE_TARGET_DATE_OPTIONS,
  CHANGE_TYPE_OPTIONS,
} from '../constants';
import {
  filterChangeMockEntries,
  formatMockDate,
  getChangeDashboardPath,
  getChangeCreatePath,
  getChangeDetailPath,
  getChangeRelativeTargetLabel,
  getChangeTabCounts,
  getFilterOptionLabel,
  isOverdue,
  sortChangeMockEntries,
} from '../helpers';
import type { TChangeDashboardScope, TChangeDashboardTab, TChangeMockConfig, TChangeMockEntry } from '../types';
import ChangeStatusBadge from './ChangeStatusBadge';

const useStyles = makeStyles({
  content: {
    maxWidth: '1536px',
  },
  actionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  filterStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  filterLabel: {
    width: '72px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  searchInput: {
    width: '360px',
    maxWidth: '100%',
  },
  filterControl: {
    width: '200px',
    maxWidth: '100%',
  },
  narrowFilterControl: {
    width: '180px',
    maxWidth: '100%',
  },
  clearButton: {
    minWidth: 'fit-content',
  },
  secondaryText: {
    color: tokens.colorNeutralForeground3,
  },
  overdue: {
    color: tokens.colorStatusDangerForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  ownerLayout: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

type TFilters = {
  search: string;
  changeType: string;
  owner: string;
  status: string;
  targetDate: string;
};

type TProps = {
  folder: TFolder;
  fullPath: TPath;
  config: TChangeMockConfig;
  scope: TChangeDashboardScope;
};

const emptyFilters: TFilters = {
  search: '',
  changeType: '',
  owner: '',
  status: '',
  targetDate: '',
};

const ChangeDropdown = ({
  label,
  value,
  emptyLabel,
  options,
  className,
  onChange,
}: {
  label: string;
  value: string;
  emptyLabel: string;
  options: string[];
  className?: string;
  onChange: (value: string) => void;
}) => (
  <Dropdown
    aria-label={label}
    placeholder={label}
    selectedOptions={[value]}
    value={getFilterOptionLabel(value, emptyLabel)}
    onOptionSelect={(_, data) => onChange((data.optionValue as string | undefined) ?? '')}
    className={className}
  >
    {options.map((option) => (
      <Option key={option || emptyLabel} value={option} text={getFilterOptionLabel(option, emptyLabel)}>
        {getFilterOptionLabel(option, emptyLabel)}
      </Option>
    ))}
  </Dropdown>
);

type TColumnId = 'change' | 'type' | 'status' | 'targetDate' | 'owner';

const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    change: { defaultWidth: 320, minWidth: 180 },
    type: { defaultWidth: 190, minWidth: 140 },
    status: { defaultWidth: 130, minWidth: 110 },
    targetDate: { defaultWidth: 150, minWidth: 120 },
    owner: { defaultWidth: 210, minWidth: 150 },
  },
};

const CHANGE_DASHBOARD_TABLE_ID = 'change-dashboard-table';

const ChangesTable = ({ items, onOpen }: { items: TChangeMockEntry[]; onOpen: (change: TChangeMockEntry) => void }) => {
  const classes = useStyles();
  const tableClasses = useItemTableStyles();
  const columnIds: TColumnId[] = useMemo(() => ['change', 'type', 'status', 'targetDate', 'owner'], []);
  const columns = useMemo(
    () => columnIds.map((columnId) => createTableColumn<TChangeMockEntry>({ columnId })),
    [columnIds]
  );
  const { columnSizing_unstable, getRows } = useTableFeatures(
    { columns, items, getRowId: (item) => item.changeNumber },
    [usePersistedColumnSizing(CHANGE_DASHBOARD_TABLE_ID, columnSizingParams)]
  );
  const rows = getRows();

  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} aria-label="Changes" {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('change')}>
              <Body1Strong>Change</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('type')}>
              <Body1Strong>Type</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('status')}>
              <Body1Strong>Status</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('targetDate')}>
              <Body1Strong>Target date</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('owner')}>
              <Body1Strong>Owner</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item }) => (
            <TableRow key={item.changeNumber}>
              <SmartTableCell
                cellProps={columnSizing_unstable.getTableCellProps('change')}
                variant={{ kind: 'link', href: '#', label: item.changeTitle }}
                openMode="sameTab"
                tooltip={{ threshold: 70 }}
                onLinkClick={() => onOpen(item)}
                stackedBelow={<Caption1 className={classes.secondaryText}>{item.changeNumber}</Caption1>}
              />
              <TableCell {...columnSizing_unstable.getTableCellProps('type')}>
                <Body1>{item.changeType}</Body1>
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('status')}>
                <ChangeStatusBadge status={item.status} />
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('targetDate')}>
                <div className="flex flex-col gap-1">
                  <Body1 className={isOverdue(item) ? classes.overdue : undefined}>
                    {formatMockDate(item.targetDate)}
                  </Body1>
                  <Caption1 className={isOverdue(item) ? classes.overdue : classes.secondaryText}>
                    {getChangeRelativeTargetLabel(item)}
                  </Caption1>
                </div>
              </TableCell>
              <TableCell {...columnSizing_unstable.getTableCellProps('owner')}>
                <div className={classes.ownerLayout}>
                  <Avatar name={item.owner} color="colorful" size={28} />
                  <Body1 className="truncate">{item.owner}</Body1>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

const ChangeDashboard = ({ folder, fullPath, config, scope }: TProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<TChangeDashboardTab>('all');
  const [filters, setFilters] = useState<TFilters>(emptyFilters);

  const changes = useMemo(() => sortChangeMockEntries(config.changes), [config.changes]);
  const tabCounts = useMemo(() => getChangeTabCounts(changes), [changes]);
  const visibleChanges = useMemo(
    () => filterChangeMockEntries(changes, { selectedTab, ...filters }),
    [changes, filters, selectedTab]
  );

  const handleOpenChange = (change: TChangeMockEntry) => {
    navigate(getChangeDetailPath(change.changeNumber, scope === 'product' ? folder._id : undefined));
  };

  const resetFilters = () => {
    setSelectedTab('all');
    setFilters(emptyFilters);
  };

  const hasFilters =
    selectedTab !== 'all' ||
    filters.search ||
    filters.changeType ||
    filters.owner ||
    filters.status ||
    filters.targetDate;

  return (
    <div className="flex flex-1 h-full">
      <FoldersNavDrawer folder={folder} fullPath={fullPath} selectedValue="changes" />

      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          <div className={mergeClasses('flex flex-col gap-5 pt-4 pb-16 pl-5 pr-8', classes.content)}>
            <Path items={useFolderPathAsBreadcrumbs(fullPath)} hasCurrent size="large" />

            <div className={classes.actionsRow}>
              <Link to={getChangeCreatePath(scope === 'product' ? folder._id : undefined)}>
                <Button appearance="primary" size="medium" icon={<AddFilled />}>
                  Create
                </Button>
              </Link>
              {/*scope === 'product' && (
                <Link to={getChangeDashboardPath()}>
                  <Button appearance="secondary" size="medium" icon={<OpenRegular />}>
                    View all company changes
                  </Button>
                </Link>
              )*/}
            </div>

            <div className={classes.filterStack}>
              <div className={classes.filterRow}>
                <Caption1 className={classes.filterLabel}>Changes</Caption1>
                <Input
                  contentBefore={<SearchRegular />}
                  placeholder="Change number or title"
                  value={filters.search}
                  onChange={(_, data) => setFilters((current) => ({ ...current, search: data.value }))}
                  className={classes.searchInput}
                />
                <ChangeDropdown
                  label="Change type"
                  value={filters.changeType}
                  emptyLabel="All types"
                  options={CHANGE_TYPE_OPTIONS}
                  onChange={(changeType) => setFilters((current) => ({ ...current, changeType }))}
                  className={classes.filterControl}
                />
                <ChangeDropdown
                  label="Owner"
                  value={filters.owner}
                  emptyLabel="All owners"
                  options={CHANGE_OWNER_OPTIONS}
                  onChange={(owner) => setFilters((current) => ({ ...current, owner }))}
                  className={classes.filterControl}
                />
              </div>
              <div className={classes.filterRow}>
                <Caption1 className={classes.filterLabel}>Status</Caption1>
                <ChangeDropdown
                  label="Status"
                  value={filters.status}
                  emptyLabel="All statuses"
                  options={CHANGE_STATUS_OPTIONS}
                  onChange={(status) => setFilters((current) => ({ ...current, status }))}
                  className={classes.narrowFilterControl}
                />
                <ChangeDropdown
                  label="Target date"
                  value={filters.targetDate}
                  emptyLabel="Any date"
                  options={CHANGE_TARGET_DATE_OPTIONS}
                  onChange={(targetDate) => setFilters((current) => ({ ...current, targetDate }))}
                  className={classes.narrowFilterControl}
                />
                <Button
                  appearance="subtle"
                  disabled={!hasFilters}
                  onClick={resetFilters}
                  className={classes.clearButton}
                >
                  Clear filters
                </Button>
              </div>
            </div>

            <TabList
              selectedValue={selectedTab}
              onTabSelect={(_, data) => setSelectedTab(data.value as TChangeDashboardTab)}
            >
              {CHANGE_DASHBOARD_TABS.map((tab) => (
                <Tab key={tab.value} value={tab.value}>
                  {tab.label} ({tabCounts[tab.value]})
                </Tab>
              ))}
            </TabList>

            {changes.length === 0 ? (
              <div className={classes.emptyState}>
                <Body1>No controlled changes have been created for this product.</Body1>
                <div className="mt-3">
                  <Link to={getChangeCreatePath(scope === 'product' ? folder._id : undefined)}>
                    <Button appearance="primary" icon={<AddFilled />}>
                      Create change
                    </Button>
                  </Link>
                </div>
              </div>
            ) : visibleChanges.length === 0 ? (
              <div className={classes.emptyState}>
                <Body1>No changes match the selected filters.</Body1>
                <div className="mt-3">
                  <Button appearance="secondary" onClick={resetFilters}>
                    Clear filters
                  </Button>
                </div>
              </div>
            ) : (
              <ChangesTable items={visibleChanges} onOpen={handleOpenChange} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeDashboard;
